/**
 * Vanntankene i en blomsterkasse. 1.0 har ett reservoar (IKEA-boksen) med én laser.
 * 2.0 stor har to skuffer med hver sin laser, sett forfra: venstre (laser 1,
 * `vann_avstand_mm`) og høyre (laser 2, `vann2_avstand_mm`). Hver tank har egen
 * kalibrering (tom/full i `potter`) og vanner sin halvdel av kassa. Hvilke tanker
 * kassa har, står i sensorvalget (lib/sensorer.ts: vann og vann2).
 */
import type { Potte } from './database.types';
import type { SensorOppsett } from './sensorer';
import { vannNivaProsent } from './utils';
import { beregnVannTrend, TANK_LITER, type VannTrend } from './trend';

/** Liter per skuff i 2.0 stor ved MAKS (3d-modell/claude-ai/2.0-stor/README.md). */
export const SKUFF_LITER = 1.96;

export interface Tank {
  nr: 1 | 2;
  /** «Vannreservoar», eller «Venstre skuff» / «Høyre skuff» når kassa har to. */
  navn: string;
  /** Grafserien (lib/grafer.ts). */
  serie: 'vann' | 'vann2';
  /** Kolonnene i `potter` med tankens kalibrering. */
  tomFelt: 'vann_tom_mm' | 'vann2_tom_mm';
  fullFelt: 'vann_full_mm' | 'vann2_full_mm';
  /** Lagret kalibrering i mm. null = standard (utils: VANN_TOM_MM / VANN_FULL_MM). */
  tomMm: number | null;
  fullMm: number | null;
  /** Volumet fra tom til full. */
  liter: number;
  /** Jordkanalene (0–3 = jord1–4) tanken vanner: hele kassa, eller sin halvdel i stor. */
  jordKanaler: number[];
}

type TankKalibrering = Pick<Potte, 'vann_tom_mm' | 'vann_full_mm' | 'vann2_tom_mm' | 'vann2_full_mm'>;
type AvstandRad = { vann_avstand_mm?: number | null; vann2_avstand_mm?: number | null };

/** Tankene kassa har, venstre før høyre. */
export function kasseTanker(p: TankKalibrering, o: Pick<SensorOppsett, 'vann' | 'vann2'>): Tank[] {
  const ut: Tank[] = [];
  if (o.vann) {
    ut.push({
      nr: 1,
      navn: o.vann2 ? 'Venstre skuff' : 'Vannreservoar',
      serie: 'vann',
      tomFelt: 'vann_tom_mm',
      fullFelt: 'vann_full_mm',
      tomMm: p.vann_tom_mm ?? null,
      fullMm: p.vann_full_mm ?? null,
      liter: o.vann2 ? SKUFF_LITER : TANK_LITER,
      jordKanaler: o.vann2 ? [0, 1] : [0, 1, 2, 3],
    });
  }
  if (o.vann2) {
    ut.push({
      nr: 2,
      navn: 'Høyre skuff',
      serie: 'vann2',
      tomFelt: 'vann2_tom_mm',
      fullFelt: 'vann2_full_mm',
      tomMm: p.vann2_tom_mm ?? null,
      fullMm: p.vann2_full_mm ?? null,
      liter: SKUFF_LITER,
      jordKanaler: [2, 3],
    });
  }
  return ut;
}

/** Tankens rå laseravstand i en avlesning. */
export function tankAvstand(t: Tank, rad: AvstandRad | null | undefined): number | null {
  return (t.nr === 1 ? rad?.vann_avstand_mm : rad?.vann2_avstand_mm) ?? null;
}

export function tankProsent(t: Tank, rad: AvstandRad | null | undefined): number | null {
  return vannNivaProsent(tankAvstand(t, rad), t.tomMm ?? undefined, t.fullMm ?? undefined);
}

export interface TankStatus {
  tank: Tank;
  pct: number | null;
  trend: VannTrend;
}

/** Nivå og forbrukstrend for én tank, med tankens egen kalibrering og volum. */
export function tankStatus(
  t: Tank,
  siste: AvstandRad | null | undefined,
  historikk: (AvstandRad & { registrert_at: string | null })[],
): TankStatus {
  const pct = tankProsent(t, siste);
  const rader = historikk.map((r) => ({ registrert_at: r.registrert_at, vann_avstand_mm: tankAvstand(t, r) }));
  return { tank: t, pct, trend: beregnVannTrend(rader, pct, t.tomMm ?? undefined, t.fullMm ?? undefined, t.liter) };
}

/** « i høyre skuff» når kassa har to tanker, ellers ingenting (som før). */
export function iTanken(t: Tank, antall: number): string {
  return antall > 1 ? ` i ${t.navn.toLowerCase()}` : '';
}
